import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";

export interface LibraryFolder {
  id: string;
  userId: string;
  name: string;
  parentId: string | null;
  color: string | null;
  icon: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface InsertFolder {
  name: string;
  parentId?: string | null;
  color?: string | null;
  icon?: string | null;
  sortOrder?: number;
}

export interface UpdateFolder {
  name?: string;
  parentId?: string | null;
  color?: string | null;
  icon?: string | null;
  sortOrder?: number;
}

export interface FolderTreeNode extends LibraryFolder {
  children: FolderTreeNode[];
  itemCount: number;
  expanded?: boolean;
}

class FoldersService {
  private generateId(): string {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substring(2, 9);
    return `folder_${timestamp}${randomPart}`;
  }

  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured. Please try again later.");
    }
    const session = await getCurrentSession();
    if (!session?.user) {
      throw new Error("User not authenticated. Please log in.");
    }
    return session.user.id;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapToFolder(row: any): LibraryFolder {
    return {
      id: row.id,
      userId: row.userId || row.user_id,
      name: row.name,
      parentId: row.parentId || row.parent_id,
      color: row.color,
      icon: row.icon,
      sortOrder: row.sortOrder ?? row.sort_order ?? 0,
      createdAt: row.createdAt || row.created_at,
      updatedAt: row.updatedAt || row.updated_at,
    };
  }

  /**
   * Get folders for the current user.
   * @param workspaceId - When undefined: no workspace filter (all folders). When null or "personal": personal only (workspace_id IS NULL). When set: folders for that workspace.
   */
  async getFolders(workspaceId?: string | null): Promise<LibraryFolder[]> {
    const userId = await this.getCurrentUserId();

    // Treat "personal" string as null so Personal scope shows folders with workspace_id IS NULL
    const resolvedWorkspaceId =
      workspaceId === "personal" || workspaceId === "" ? null : workspaceId;

    let query = supabase
      .from("document_folders")
      .select("*")
      .eq("user_id", userId)
      .order("position", { ascending: true });
    if (resolvedWorkspaceId !== undefined) {
      if (resolvedWorkspaceId != null && resolvedWorkspaceId !== "") {
        query = query.eq("workspaceId", resolvedWorkspaceId);
      } else {
        query = query.is("workspaceId", null);
      }
    }
    const { data, error } = await query;

    if (error) {
      if (error.code === "PGRST205" || error.message.includes("does not exist") || error.code === "42P01") {
        console.warn("Document folders table not yet created. Run the migration to enable folder functionality.");
        return [];
      }
      console.error("Error fetching folders:", error);
      throw new Error(`Failed to fetch folders: ${error.message}`);
    }

    const folders = (data || []).map((row: any) => this.mapToFolder(row));
    console.log("[foldersService.getFolders] workspaceId:", workspaceId, "resolved:", resolvedWorkspaceId, "folderCount:", folders.length, "folderIds:", folders.map((f) => f.id));
    return folders;
  }

  async getFolder(id: string): Promise<LibraryFolder | null> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("document_folders")
      .select("*")
      .eq("id", id)
      .eq("user_id", userId)
      .single();

    if (error) {
      if (error.code === "PGRST116") return null;
      console.error("Error fetching folder:", error);
      throw new Error(`Failed to fetch folder: ${error.message}`);
    }

    return this.mapToFolder(data);
  }

  /**
   * Create a folder. When workspaceId is provided, the folder belongs to that workspace; when null, personal.
   */
  async createFolder(folderData: InsertFolder, workspaceId?: string | null): Promise<LibraryFolder> {
    const userId = await this.getCurrentUserId();

    if (!userId) {
      throw new Error("User not authenticated. Please log in.");
    }

    const folderToInsert: Record<string, unknown> = {
      id: this.generateId(),
      user_id: userId,
      name: folderData.name,
      parent_id: folderData.parentId || null,
      position: String(folderData.sortOrder ?? 0),
    };
    if (workspaceId != null && workspaceId !== "") {
      folderToInsert.workspaceId = workspaceId;
    } else {
      folderToInsert.workspaceId = null;
    }

    const { data, error } = await supabase
      .from("document_folders")
      .insert(folderToInsert)
      .select()
      .single();

    if (error) {
      console.error("Error creating folder:", error);
      console.error("Folder data attempted:", folderToInsert);
      throw new Error(`Failed to create folder: ${error.message}`);
    }

    return this.mapToFolder(data);
  }

  async updateFolder(id: string, updates: UpdateFolder): Promise<LibraryFolder> {
    const userId = await this.getCurrentUserId();

    const updateData: any = { updated_at: new Date().toISOString() };
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.parentId !== undefined) updateData.parent_id = updates.parentId;
    if (updates.color !== undefined) updateData.color = updates.color;
    if (updates.icon !== undefined) updateData.icon = updates.icon;
    if (updates.sortOrder !== undefined) updateData.position = String(updates.sortOrder);

    const { data, error } = await supabase
      .from("document_folders")
      .update(updateData)
      .eq("id", id)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error updating folder:", error);
      throw new Error(`Failed to update folder: ${error.message}`);
    }

    return this.mapToFolder(data);
  }

  async deleteFolder(id: string): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("document_folders")
      .delete()
      .eq("id", id)
      .eq("user_id", userId);

    if (error) {
      console.error("Error deleting folder:", error);
      throw new Error(`Failed to delete folder: ${error.message}`);
    }
  }

  async moveToFolder(documentId: string, folderId: string | null): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("knowledge_base")
      .update({ folder_id: folderId, updated_at: new Date().toISOString() })
      .eq("id", documentId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error moving document to folder:", error);
      throw new Error(`Failed to move document: ${error.message}`);
    }
  }

  buildFolderTree(folders: LibraryFolder[]): LibraryFolder[] {
    const rootFolders = folders.filter(f => !f.parentId);
    return rootFolders.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  getChildFolders(folders: LibraryFolder[], parentId: string): LibraryFolder[] {
    return folders
      .filter(f => f.parentId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Get folder tree. Pass workspaceId to scope to that workspace or personal (null).
   */
  async getFolderTree(workspaceId?: string | null): Promise<FolderTreeNode[]> {
    const folders = await this.getFolders(workspaceId);
    const userId = await this.getCurrentUserId();

    const { data: sourceCounts } = await supabase
      .from("sources")
      .select("folder_id")
      .eq("user_id", userId)
      .not("folder_id", "is", null);

    const { data: docCounts } = await supabase
      .from("mastermind_documents")
      .select("folder_id")
      .eq("user_id", userId)
      .not("folder_id", "is", null);

    const itemCountMap: Record<string, number> = {};
    for (const source of sourceCounts || []) {
      const folderId = (source as { folder_id: string }).folder_id;
      if (folderId) {
        itemCountMap[folderId] = (itemCountMap[folderId] || 0) + 1;
      }
    }
    for (const doc of docCounts || []) {
      const folderId = (doc as { folder_id: string }).folder_id;
      if (folderId) {
        itemCountMap[folderId] = (itemCountMap[folderId] || 0) + 1;
      }
    }

    const folderMap = new Map<string, FolderTreeNode>();
    for (const folder of folders) {
      folderMap.set(folder.id, {
        ...folder,
        children: [],
        itemCount: itemCountMap[folder.id] || 0,
        expanded: false,
      });
    }

    const rootNodes: FolderTreeNode[] = [];
    for (const node of folderMap.values()) {
      if (node.parentId && folderMap.has(node.parentId)) {
        folderMap.get(node.parentId)!.children.push(node);
      } else {
        rootNodes.push(node);
      }
    }

    const sortNodes = (nodes: FolderTreeNode[]) => {
      nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
      for (const node of nodes) {
        if (node.children.length > 0) {
          sortNodes(node.children);
        }
      }
    };

    sortNodes(rootNodes);
    return rootNodes;
  }

  async getDescendantFolderIds(folderId: string): Promise<string[]> {
    const userId = await this.getCurrentUserId();
    const descendants: string[] = [];

    const { data: children } = await supabase
      .from("document_folders")
      .select("id")
      .eq("parent_id", folderId)
      .eq("user_id", userId);

    if (children) {
      for (const child of children) {
        descendants.push(child.id);
        const childDescendants = await this.getDescendantFolderIds(child.id);
        descendants.push(...childDescendants);
      }
    }

    return descendants;
  }

  async moveFolder(folderId: string, newParentId: string | null): Promise<LibraryFolder> {
    const userId = await this.getCurrentUserId();

    if (newParentId) {
      const descendants = await this.getDescendantFolderIds(folderId);
      if (descendants.includes(newParentId) || folderId === newParentId) {
        throw new Error("Cannot move a folder into itself or its descendants");
      }
    }

    const { data, error } = await supabase
      .from("document_folders")
      .update({
        parent_id: newParentId,
        updated_at: new Date().toISOString(),
      })
      .eq("id", folderId)
      .eq("user_id", userId)
      .select()
      .single();

    if (error) {
      console.error("Error moving folder:", error);
      throw new Error(`Failed to move folder: ${error.message}`);
    }

    return this.mapToFolder(data);
  }

  async reorderFolders(folderIds: string[], parentId: string | null): Promise<void> {
    const userId = await this.getCurrentUserId();

    for (let i = 0; i < folderIds.length; i++) {
      await supabase
        .from("document_folders")
        .update({
          position: String(i),
          parent_id: parentId,
          updated_at: new Date().toISOString(),
        })
        .eq("id", folderIds[i])
        .eq("user_id", userId);
    }
  }

  async searchFolders(query: string): Promise<LibraryFolder[]> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase
      .from("document_folders")
      .select("*")
      .eq("user_id", userId)
      .ilike("name", `%${query}%`)
      .order("name", { ascending: true });

    if (error) {
      console.error("Error searching folders:", error);
      throw new Error(`Failed to search folders: ${error.message}`);
    }

    return (data || []).map((row: any) => this.mapToFolder(row));
  }

  async deleteFolderWithContents(id: string, deleteContents: boolean = false): Promise<void> {
    const userId = await this.getCurrentUserId();

    if (deleteContents) {
      const descendants = await this.getDescendantFolderIds(id);
      const allFolderIds = [id, ...descendants];

      await supabase
        .from("sources")
        .delete()
        .in("folder_id", allFolderIds)
        .eq("user_id", userId);

      await supabase
        .from("mastermind_documents")
        .delete()
        .in("folder_id", allFolderIds)
        .eq("user_id", userId);

      for (const folderId of [...descendants.reverse(), id]) {
        await supabase
          .from("document_folders")
          .delete()
          .eq("id", folderId)
          .eq("user_id", userId);
      }
    } else {
      await supabase
        .from("sources")
        .update({ folder_id: null })
        .eq("folder_id", id)
        .eq("user_id", userId);

      await supabase
        .from("mastermind_documents")
        .update({ folder_id: null })
        .eq("folder_id", id)
        .eq("user_id", userId);

      await supabase
        .from("document_folders")
        .update({ parent_id: null })
        .eq("parent_id", id)
        .eq("user_id", userId);

      await this.deleteFolder(id);
    }
  }

  async moveSourceToFolder(sourceId: string, folderId: string | null): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("sources")
      .update({ folder_id: folderId, updated_at: new Date().toISOString() })
      .eq("id", sourceId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error moving source to folder:", error);
      throw new Error(`Failed to move source: ${error.message}`);
    }
  }

  async moveDocumentToFolder(documentId: string, folderId: string | null): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from("mastermind_documents")
      .update({ folder_id: folderId, updated_at: new Date().toISOString() })
      .eq("id", documentId)
      .eq("user_id", userId);

    if (error) {
      console.error("Error moving document to folder:", error);
      throw new Error(`Failed to move document: ${error.message}`);
    }
  }

  async getItemsInFolder(folderId: string | null): Promise<{
    sources: any[];
    documents: any[];
  }> {
    const userId = await this.getCurrentUserId();

    const sourceQuery = folderId 
      ? supabase.from("sources").select("*").eq("folder_id", folderId).eq("user_id", userId)
      : supabase.from("sources").select("*").is("folder_id", null).eq("user_id", userId);

    const docQuery = folderId
      ? supabase.from("mastermind_documents").select("*").eq("folder_id", folderId).eq("user_id", userId)
      : supabase.from("mastermind_documents").select("*").is("folder_id", null).eq("user_id", userId);

    const [{ data: sources }, { data: documents }] = await Promise.all([
      sourceQuery,
      docQuery,
    ]);

    return {
      sources: sources || [],
      documents: documents || [],
    };
  }
}

export const foldersService = new FoldersService();
